import { useEffect, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import { RenovationLabel, RenovationLabelType } from "shared/types/renovation";
import { Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Input, Select, SelectItem, Button } from "@heroui/react";
import { Plus } from "@phosphor-icons/react";

export const RenovationLabelsTab = () => {
    const [labels, setLabels] = useState<RenovationLabel[]>([]);
    const [newLabel, setNewLabel] = useState({
        name: "",
        type: RenovationLabelType.GENERAL
    });
    const { session } = useSession();

    useEffect(() => {
        fetchLabels();
    }, []);

    const fetchLabels = async () => {
        try {
            const response = await fetchWithAuth(
                `${API_URL}/renovation/labels`,
                { method: "GET" },
                session
            );
            if (!response.ok) throw new Error("Failed to fetch labels");
            const data = await response.json();
            setLabels(data);
        } catch (error) {
            console.error("Error fetching labels:", error);
            toast.error("Failed to fetch labels");
        }
    };

    const handleCreateLabel = async () => {
        if (!newLabel.name.trim()) {
            toast.error("Geef de categorie een naam");
            return;
        }

        try {
            const response = await fetchWithAuth(
                `${API_URL}/renovation/label`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(newLabel)
                },
                session
            );
            if (!response.ok) throw new Error("Failed to create label");
            
            await fetchLabels();
            setNewLabel({ name: "", type: RenovationLabelType.GENERAL });
            toast.success("Categorie succesvol aangemaakt");
        } catch (error) {
            console.error("Error creating label:", error);
            toast.error("Failed to create label");
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
                <h3 className="text-lg font-medium">Renovatie categorieën</h3>
                <p className="text-sm text-neutral-500">
                    Beheer de categorieën die gebruikt kunnen worden voor renovatiebudgetten en projectonderdelen
                </p>
            </div>

            <div className="flex items-end gap-4 rounded-lg border border-neutral-200 bg-white p-4">
                <Input
                    label="Categorienaam"
                    placeholder="Voer een categorienaam in"
                    value={newLabel.name}
                    onChange={(e) => setNewLabel({ ...newLabel, name: e.target.value })}
                    className="flex-1"
                />
                <Select
                    label="Type"
                    selectedKeys={[newLabel.type]}
                    onChange={(e) => setNewLabel({ ...newLabel, type: e.target.value as RenovationLabelType })}
                    className="w-64"
                >
                    {Object.values(RenovationLabelType).map((type) => (
                        <SelectItem key={type}>
                            {type.charAt(0) + type.slice(1).toLowerCase()}
                        </SelectItem>
                    ))}
                </Select>
                <Button
                    onClick={handleCreateLabel}
                    className="bg-moss-primary text-white"
                    startContent={<Plus size={16} />}
                >
                    Toevoegen
                </Button>
            </div>

            <Table
                aria-label="Renovation labels"
                classNames={{
                    wrapper: "shadow-none rounded-lg border border-neutral-200",
                }}
            >
                <TableHeader>
                    <TableColumn>CATEGORIENAAM</TableColumn>
                    <TableColumn>TYPE</TableColumn>
                </TableHeader>
                <TableBody>
                    {labels.map((label) => (
                        <TableRow key={label.id}>
                            <TableCell>{label.name}</TableCell>
                            <TableCell>
                                <span className="rounded-full bg-moss-primary/10 px-2 py-1 text-sm font-medium text-moss-primary">
                                    {label.type.charAt(0) + label.type.slice(1).toLowerCase()}
                                </span>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}; 